import _ from 'lodash';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { usePaymentAPI } from '../../apis/payment-api';
import { PaymentSelectAmountModalBody } from '../../components/payment/payment-select-amount-modal-body';
import { useErrorHandler } from '../../hooks/use-error-handler';
import { Feature, useFeature } from '../../hooks/use-feature';
import { useStorage } from '../../hooks/use-storage';
import { useSwal } from '../../hooks/use-swal';
import { layoutDefaults } from '../../utils/layout-defaults';
import {
  parseOrdersQuantity,
  parsePayableOrders,
  parseTip,
  parseTotal,
} from '../../utils/manager/order-manager';
import { useAnalytics } from '../../utils/setup/analytics-setup';

function usePaymentSelectAmountModal() {
  const storage = useStorage();
  const { menuInfo, setSession, spot, language } = storage;
  const { analyticsPageView } = useAnalytics();
  const { handleErrorOnModal } = useErrorHandler();
  const { calculateRemainingFeeFromRemote } = usePaymentAPI();
  const { swal } = useSwal({ storage });
  const { t } = useTranslation();
  const feature = useFeature();

  // const [remainingFees, setRemainingFees, remainingFeesRef] = useStateRef();

  const ordersRef = useRef();
  const valuesRef = useRef();
  const tipSuggestionRef = useRef();
  const selectedOrdersRef = useRef();
  const remainingFeesRef = useRef();

  const methodRef = useRef();
  const amountKeyRef = useRef();
  const tipAmountRef = useRef();
  const remainingFeeAmountRef = useRef();

  const methodLabel = t('payment.choose-way');

  // const addressable = spot?.type?.addressable ?? false;
  // const privateBill = spot?.type?.private_bill ?? false;
  // const prepaid = spot?.prepaid ?? false;

  const debounced = useDebouncedCallback(async (updateRemainingFees) => {
    await calculateAndSetRemainingFees();
    updateRemainingFees(remainingFeesRef.current);
  }, layoutDefaults.keyboardDuration);

  const amountLabel = useMemo(
    () => t(`payment.choose-what${spot?.prepaid ? '.prepaid' : ''}`),
    [spot]
  );

  async function prepare({ allOrders, selectedOrders, userOrders }) {
    methodRef.current = null;
    amountKeyRef.current = null;
    tipAmountRef.current = null;

    ordersRef.current = {
      allOrders: parsePayableOrders(allOrders) ?? [],
      selectedOrders: parsePayableOrders(selectedOrders) ?? [],
      userOrders: parsePayableOrders(userOrders) ?? [],
    };

    const orders = ordersRef.current;

    valuesRef.current = {
      allOrders: parseTotal(orders.allOrders),
      selectedOrders: parseTotal(orders.selectedOrders),
      userOrders: parseTotal(orders.userOrders),
    };

    const tipEnabled = feature.isFeatureEnabled(Feature.ON_SITE_TIP);
    const suggestion = menuInfo?.on_site?.tip?.suggestion;

    tipSuggestionRef.current = {
      allOrders: parseTip(orders.allOrders, tipEnabled, suggestion),
      selectedOrders: parseTip(orders.selectedOrders, tipEnabled, suggestion),
      userOrders: parseTip(orders.userOrders, tipEnabled, suggestion),
    };

    await calculateAndSetRemainingFees();
  }

  async function calculateAndSetRemainingFees() {
    const addressable = spot?.type?.addressable ?? false;

    const pAllOrders = calculateRemainingFeeFromRemote({
      orders_amount: valuesRef.current.allOrders,
      tip: addressable
        ? 0
        : tipAmountRef.current ?? tipSuggestionRef.current.allOrders,
    });

    const pSelectedOrders = calculateRemainingFeeFromRemote({
      orders_amount: valuesRef.current.selectedOrders,
      tip: addressable
        ? 0
        : tipAmountRef.current ?? tipSuggestionRef.current.selectedOrders,
    });

    const pUserOrders = calculateRemainingFeeFromRemote({
      orders_amount: valuesRef.current.userOrders,
      tip: addressable
        ? 0
        : tipAmountRef.current ?? tipSuggestionRef.current.userOrders,
    });

    const [rAllOrders, rSelectedOrders, rUserOrders] = await Promise.all([
      pAllOrders,
      pSelectedOrders,
      pUserOrders,
    ]);

    const allOrders = rAllOrders?.data;
    const selectedOrders = rSelectedOrders?.data;
    const userOrders = rUserOrders?.data;

    remainingFeesRef.current = {
      allOrders,
      selectedOrders,
      userOrders,
    };
  }

  function didOpen() {
    analyticsPageView('Payment Ammount Selection');
    setSession(null);
  }

  async function show(
    {
      showBackButton,
      wasBackButtonClicked,
      prepareOnPreConfirm,
      prepareOnPreDeny,
    } = {
      wasBackButtonClicked: false,
    }
  ) {
    let tipPercent;

    const addressable = spot?.type?.addressable ?? false;
    const privateBill = spot?.type?.private_bill ?? false;
    const prepaid = spot?.prepaid ?? false;

    const result = await swal.fire({
      html: (
        <PaymentSelectAmountModalBody
          amountLabel={amountLabel}
          methodLabel={methodLabel}
          addressable={addressable}
          defaultMethod={methodRef.current}
          defaultAmountKey={amountKeyRef.current}
          defaultTipAmount={tipAmountRef.current}
          defaultRemainingFeeAmount={remainingFeeAmountRef.current}
          remainingFees={remainingFeesRef.current}
          values={valuesRef.current}
          prepaid={prepaid}
          privateBill={privateBill}
          allOrders={ordersRef.current.allOrders}
          selectedOrders={ordersRef.current.selectedOrders}
          feature={feature}
          menuInfo={menuInfo}
          language={language}
          inputOptions={getInputOptions()}
          onChangeTip={async (value, percent, updateRemainingFees) => {
            tipAmountRef.current = value;
            tipPercent = percent;

            debounced(updateRemainingFees);
          }}
          onSelectMethod={(key) => {
            methodRef.current = key;
          }}
          onSelectAmount={(key) => {
            amountKeyRef.current = key;
          }}
          onSelectRemainingFeeAmount={(amount) => {
            remainingFeeAmountRef.current = amount;
          }}
        />
      ),
      didOpen,
      showCancelButton: false,
      showDenyButton: true,
      showLoaderOnConfirm: true,
      showLoaderOnDeny: true,
      confirmButtonText: t('continue'),
      denyButtonText: showBackButton ? t('back') : t('cancel'),
      allowOutsideClick: true,
      preConfirm: () =>
        preConfirm({
          tipPercent,
          prepareOnPreConfirm,
        }),
      preDeny: () => preDeny({ prepareOnPreDeny }),
      showClass:
        wasBackButtonClicked || addressable
          ? null
          : {
              popup: 'animate__animated animate__slideInLeft animate__faster',
            },
    });

    result.method = methodRef.current;
    result.amount = amountKeyRef.current;
    result.tipAmount = tipAmountRef.current;
    result.tipPercent = tipPercent;
    result.orders = selectedOrdersRef.current;

    return result;
  }

  const preConfirm = async ({ prepareOnPreConfirm }) => {
    swal.disableButtons();

    if (amountKeyRef.current === 'empty') {
      swal.enableButtons();
      return true;
    }

    if (methodRef.current == null) {
      swal.showValidationMessage(methodLabel);
      swal.enableButtons();
      return false;
    }

    const addressable = spot?.type?.addressable ?? false;

    if (
      amountKeyRef.current == null &&
      methodRef.current !== 'offline' &&
      !addressable
    ) {
      swal.showValidationMessage(amountLabel);
      swal.enableButtons();
      return false;
    }

    selectedOrdersRef.current = getSelectedOrders(amountKeyRef.current);

    try {
      await prepareOnPreConfirm?.call(this, {
        orders: ordersRef.current[amountKeyRef.current],
        tipAmount: tipAmountRef.current,
        method: methodRef.current,
      });
    } catch (error) {
      handleErrorOnModal(error);
      return false;
    }

    swal.enableButtons();
    return true;
  };

  async function preDeny({ prepareOnPreDeny }) {
    try {
      await prepareOnPreDeny?.call(this);
    } catch (error) {
      handleErrorOnModal(error);
      return false;
    }

    return true;
  }

  function getSelectedOrders(choice) {
    const addressable = spot?.type?.addressable ?? false;

    if (addressable === true) {
      return ordersRef.current.allOrders;
    }

    return ordersRef.current[choice];
  }

  const getInputOptions = () => {
    const ammount = {};
    const lOrders = ordersRef.current;
    const lValues = valuesRef.current;
    const lTipSuggestion = tipSuggestionRef.current;

    if (!lOrders || !lValues || !lTipSuggestion) return null;

    if (_.isEmpty(lOrders.selectedOrders)) {
      ammount.empty = {
        label: t(`payment.some${spot?.prepaid ? '.prepaid' : ''}`),
        value: null,
        tipSuggestion: null,
      };
    } else {
      const selectedLabel = t('payment.selected', {
        count: lOrders.selectedOrders.length,
      });

      ammount.selectedOrders = {
        label: `${selectedLabel} ${t('payment.pay.amount-product', { count: parseOrdersQuantity(lOrders.selectedOrders) })}`,
        value: lValues.selectedOrders,
        tipSuggestion: lTipSuggestion.selectedOrders,
      };
    }

    if (
      !_.isEqual(lOrders.userOrders, lOrders.allOrders) &&
      !_.isEqual(lOrders.userOrders, lOrders.selectedOrders) &&
      !_.isEmpty(lOrders.userOrders) &&
      _.differenceWith(
        lOrders.userOrders,
        lOrders.selectedOrders,
        (o1, o2) => o1.id === o2.id
      ).length > 0
    ) {
      ammount.userOrders = {
        label: `${t('payment.what-ordered')}: ${t('payment.pay.amount-product', { count: parseOrdersQuantity(lOrders.userOrders) })}`,
        value: lValues.userOrders,
        tipSuggestion: lTipSuggestion.userOrders,
      };
    }

    if (!_.isEqual(lValues.selectedOrders, lValues.allOrders)) {
      ammount.allOrders = {
        label: `${t('payment.all')}: ${t('payment.pay.amount-product', { count: parseOrdersQuantity(lOrders.allOrders) })}`,
        value: lValues.allOrders,
        tipSuggestion: lTipSuggestion.allOrders,
      };
    }

    return ammount;
  };

  return {
    preparePaymentSelectAmmountModal: prepare,
    showPaymentSelectAmmountModal: show,
  };
}

export { usePaymentSelectAmountModal };
